import React, { useEffect, useState } from "react";
import { toLocalISODate } from "../../lib/dates";
import { toast } from "sonner";
import { PageHero, Section } from "../../components/Page";
import { useApi } from "../../hooks/useApi";
import api, { formatError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { RichEditor, RichView, cleanHtml, legacyToHtml } from "../../components/RichEditor";

export const policyHtml = (body) => cleanHtml(legacyToHtml(body));

export function PolicyPage({ slug, eyebrow }) {
  const { data } = useApi(`/policies/${slug}`);
  return (
    <div data-testid={`policy-${slug}`}>
      <PageHero eyebrow={eyebrow} title={data?.title || ""} subtitle={data?.updated_at ? `Last revised ${toLocalISODate(data.updated_at)}` : "Okuapemman Association of Massachusetts"} />
      <Section>
        {data && <RichView html={data.body} className="max-w-3xl text-foreground/85" testId={`policy-${slug}-body`} />}
      </Section>
    </div>
  );
}

export const Privacy = () => <PolicyPage slug="privacy" eyebrow="Your data" />;
export const Terms = () => <PolicyPage slug="terms" eyebrow="Using the platform" />;
export const Cookies = () => <PolicyPage slug="cookies" eyebrow="Your browser" />;

function PolicyEditor({ slug }) {
  const { data, reload } = useApi(`/policies/${slug}`);
  const [f, setF] = useState(null);
  useEffect(() => { if (data) setF({ title: data.title, body: legacyToHtml(data.body) }); }, [data]);
  if (!f) return null;
  const save = async () => { try { await api.put(`/admin/policies/${slug}`, f); toast.success(`${f.title} published`); reload(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  const reset = async () => { try { await api.post(`/admin/policies/${slug}/reset`); toast.success("Restored default text"); reload(); } catch (e) { toast.error(formatError(e.response?.data?.detail)); } };
  return (
    <div className="glass rounded-2xl p-5" data-testid={`policy-editor-${slug}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div><Label>Title</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className="w-72" data-testid={`policy-${slug}-title`} /></div>
        <span className="text-xs text-muted-foreground">{data.is_default ? "Using default text" : `Revised ${toLocalISODate(data.updated_at)} by ${data.updated_by}`} · <a href={`/${slug}`} target="_blank" rel="noreferrer" className="underline text-palm">view page</a></span>
      </div>
      <div className="mt-3"><RichEditor value={f.body} onChange={(body) => setF((s) => ({ ...s, body }))} rows={16} testId={`policy-${slug}-body-input`} /></div>
      <div className="mt-3 flex justify-end gap-2">
        <Button variant="ghost" onClick={reset} data-testid={`policy-${slug}-reset`}>Restore default</Button>
        <Button onClick={save} className="bg-crimson hover:bg-crimson-hover" data-testid={`policy-${slug}-save`}>Publish</Button>
      </div>
    </div>
  );
}

export function PoliciesTab() {
  return (
    <div className="space-y-6" data-testid="policies-tab">
      <p className="text-sm text-muted-foreground">Edit the public Privacy Policy, Terms of Use and Cookie Policy with the rich-text toolbar (headings, bold, lists, links). Changes publish immediately and are recorded in the audit log.</p>
      <PolicyEditor slug="privacy" />
      <PolicyEditor slug="terms" />
      <PolicyEditor slug="cookies" />
    </div>
  );
}
